import { useFormikContext } from 'formik';
import type { PaymentFormValues } from 'Src/types/payment';
import {
  type PaymentFormDisplays,
  type CommonPaymentFormDisplays,
} from 'Components/widgets/payment-info/payment-form/hooks/usePaymentFormDisplay';
import { NAPAY_PSP_ID } from 'Components/widgets/payment-info/PaymentInfo.const';
import { useAppContext } from 'Context/ReactAppContext';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import { isVoxpayProvider } from 'Components/widgets/payment-info/payment-form/paymentProvidersUtils';
import { useBookingNavbar } from 'Components/widgets/booking-navbar/useBookingNavbar';
import { useContext } from 'react';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';

export const useB2BPaymentForm = (
  props: CommonPaymentFormDisplays,
): Partial<PaymentFormDisplays> => {
  const { paymentProviders = [], ...paymentFormDisplay } = props;
  const appContext = useAppContext();
  const formik = useFormikContext<PaymentFormValues>();
  const { isIndirectBooking } = useContext(PaymentInfoContext);

  const { isCmta, isCxo, isSeller } = appContext;
  const selectedPaymentProvider = paymentProviders.find(
    ({ selectorId }) => selectorId === formik.values.selectorPaymentProviderId,
  );
  const hideContinueButton =
    isCmta &&
    selectedPaymentProvider?.paymentMethods.length !== 0 &&
    !formik.values.paymentMethodId;

  useBookingNavbar({
    hideContinueButton,
  });

  if (!isSeller) {
    return props;
  }

  const hasOnlyNaPaymentForCxo = isCxo && selectedPaymentProvider?.id === NAPAY_PSP_ID;

  const isVoxpay = isVoxpayProvider(formik.values.paymentProviderId);

  const displayB2BPaymentProvidersForm = !!(
    hasOnlyNaPaymentForCxo ||
    (isSeller && isVoxpayProvider(selectedPaymentProvider?.id)) ||
    (isCmta && paymentProviders.length && !paymentFormDisplay.displayPaymentMethodsWithConditions)
  );

  const displayPaymentProvidersForm =
    paymentFormDisplay.displayPaymentProvidersForm && !displayB2BPaymentProvidersForm;

  const displayNewPaymentProvidersImplementation = isSeller;

  const displayB2BFreeDeposit =
    paymentFormDisplay.displayFreeDeposit &&
    ((isFeatureFlipped(appContext)('opcTax') && isIndirectBooking) ||
      (isCxo && isFeatureFlipped(appContext)('exceedTotalAmount')));

  return {
    ...paymentFormDisplay,
    displayB2BPaymentPlan: true,
    displayPaymentPlan: false,
    displayNewPaymentProvidersImplementation,
    displayB2BPaymentProvidersForm,
    displayPaymentProvidersForm,
    displayPaymentMethods: paymentFormDisplay.displayPaymentMethods && !isVoxpay,
    displayB2BFreeDeposit,
    ...(displayB2BFreeDeposit ? { displayFreeDeposit: false } : {}),
  };
};
